/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useCallback, useState } from "react";
import { store, view } from "@risingstack/react-easy-state";
import KinesisVideo from "aws-sdk/clients/kinesisvideo";
import KinesisVideoSignalingChannels from "aws-sdk/clients/kinesisvideosignalingchannels";
import { SignalingClient } from "amazon-kinesis-video-streams-webrtc";
import AWS from "aws-sdk";
import config from "../config";
import "./common.css";
import FaceDetectionWrapper from "./FaceDetectionWrapper";
import "./VideoLayout.css";
import ChatWidget from "./ChatWidget";
import WelcomeScreen from "./WelcomeScreen";
import ExamTimer from "./ExamTimer";
import ExamEndScreen from "./ExamEndScreen";
import {
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaLightbulb,
} from "react-icons/fa";
import { PiUserCircleCheckBold } from "react-icons/pi";
import { BsFillPersonCheckFill, BsExclamationTriangle } from "react-icons/bs";
import { PiUserCircleBold } from "react-icons/pi";
import { PiUserCircleMinusBold } from "react-icons/pi";
import QuestionPaper from "./QuestionPaper";
import {
  requestFullscreen,
  initializeFullscreenMonitoring,
} from "../utils/securityUtils";
import RoomScanDialog from "./RoomScanDialog";
import ClarificationDialog from "./ClarificationDialog";
import IdCardDialog from "./IdCardDialog";
import { MdMeetingRoom } from "react-icons/md";
import ObjectDetectionHandler from "./ObjectDetectionHandler";
// Update AWS configuration using config instead of process.env
AWS.config.update({
  region: config.aws.region,
  credentials: {
    accessKeyId: config.aws.credentials.accessKeyId,
    secretAccessKey: config.aws.credentials.secretAccessKey,
  },
});

// Used to determine / validate options in form components:
const OPTIONS = {
  TRAVERSAL: {
    STUN_TURN: "stunTurn",
    TURN_ONLY: "turnOnly",
    DISABLED: "disabled",
  },
  ROLE: {
    MASTER: "MASTER",
    VIEWER: "VIEWER",
  },
  RESOLUTION: {
    WIDESCREEN: "widescreen",
    FULLSCREEN: "fullscreen",
  },
};

// Stores state across components (react-easy-state is super easy to use!)
const state = store({
  // Add loading state
  isInitialized: false,

  // These are config params set by the user:
  role: window.appState?.role || OPTIONS.ROLE.VIEWER,
  channelName: window.appState?.channelName?.replace(/\s+/g, "_"),
  clientId: window.appState?.clientId,
  universityId: window.appState?.universityId, // Add universityId from window.appState

  // Add mode and verification states
  mode: "VERIFICATION", // VERIFICATION, EXAM, ANSWER_UPLOAD, or EXAM_COMPLETE
  verificationStatus: "PENDING", // PENDING, VERIFIED, REJECTED
  startTime: window.appState?.startTime || null,
  endTime: window.appState?.endTime || null,
  isExamStarted: false,
  examEndReason: null,

  endpoint: null,
  // These will be true by default and not changeable
  sendVideo: true,
  sendAudio: true,
  openDataChannel: true,
  useTrickleICE: true,
  resolution: OPTIONS.RESOLUTION.WIDESCREEN,
  natTraversal: OPTIONS.TRAVERSAL.STUN_TURN,
  messageToSend: "",
  playerIsStarted: false,

  // Add behavior alerts to the global state
  behaviorAlerts: [],
  // Add recording chunk tracking
  currentChunkInfo: null,
  violationRegistry: [],

  // These are set when user starts video; a few of them are only used when you start the stream as MASTER:
  signalingClient: null,
  localStream: null,
  localView: null,
  remoteView: null,
  dataChannel: null,
  peerConnectionStatsInterval: null,
  peerConnectionByClientId: {},
  dataChannelByClientId: [],
  receivedMessages: "",
  remoteViews: {},
  videoClient: {},
  channelARN: null,
  isMasterConnected: false,
  isRecording: false, // To track recording status
  recordingInterval: null, // To store the interval reference
  accessKey: config.aws.credentials.accessKeyId,
  secretAccessKey: config.aws.credentials.secretAccessKey,
  sessionToken: "",
  region: config.aws.region,

  // Initialize state with separate message arrays
  messages: {
    broadcast: "",
    master: "",
    currentView: "broadcast", // to track which view is active
  },
});

// Make stopPlayer and handleSessionEnd available globally
window.stopPlayer = stopPlayer;
window.handleSessionEnd = (reason) => {
  console.log("[VIEWER] Global handleSessionEnd called with reason:", reason);
  const handleSessionEndCallback = async (reason) => {
    try {
      console.log("[VIEWER] Session end callback called with reason:", reason);

      // Cleanup existing resources
      if (state.localStream) {
        state.localStream.getTracks().forEach((track) => track.stop());
      }

      if (state.peerConnectionStatsInterval) {
        clearInterval(state.peerConnectionStatsInterval);
        state.peerConnectionStatsInterval = null;
      }

      if (state.signalingClient) {
        state.signalingClient.close();
        state.signalingClient = null;
      }

      if (state.peerConnection) {
        state.peerConnection.close();
        state.peerConnection = null;
      }

      // Set mode to EXAM_COMPLETE directly
      state.mode = "EXAM_COMPLETE";
      state.examEndReason = reason;

      // Stop the stream
      if (typeof stopPlayer === "function") {
        stopPlayer();
      }

      // Clear localStorage
      try {
        localStorage.removeItem("suspiciousActions");
        localStorage.removeItem("examAnswers");
        console.log("[VIEWER] Successfully cleared localStorage");
      } catch (error) {
        console.error("[VIEWER] Error clearing localStorage:", error);
      }

      // Update appState
      window.appState = {
        ...window.appState,
        examEndReason: reason,
        mode: "EXAM_COMPLETE",
        isExamStarted: false,
        examStartTime: null,
      };

      // Dispatch state change event
      window.dispatchEvent(
        new CustomEvent("examStateChange", {
          detail: {
            mode: "EXAM_COMPLETE",
            reason,
            timestamp: new Date().toISOString(),
          },
        })
      );
    } catch (error) {
      console.error("[VIEWER] Error in handleSessionEnd:", error);
    }
  };

  handleSessionEndCallback(reason);
};

// Make trackViolation function globally available
window.trackViolation = (violationType, details) => {
  return trackViolation(violationType, details);
};

// Function to get all violations for analysis
window.getViolationRegistry = () => {
  return state.violationRegistry || [];
};

// Function to get current chunk info
window.getCurrentChunkInfo = () => {
  return state.currentChunkInfo || null;
};

// Function to export violations to JSON
window.exportViolationsToJSON = () => {
  const violations = state.violationRegistry || [];
  if (violations.length === 0) {
    console.log("[VIEWER] No violations to export");
    return null;
  }

  const violationData = {
    studentId: state.clientId,
    examId: state.channelName,
    timestamp: new Date().toISOString(),
    sessionData: {
      startTime: state.startTime,
      endTime: state.endTime,
      mode: state.mode,
    },
    violations: violations,
  };

  // Create a Blob with the JSON data
  const jsonBlob = new Blob([JSON.stringify(violationData, null, 2)], {
    type: "application/json",
  });

  // Create and trigger a download link
  const url = URL.createObjectURL(jsonBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `violations_${state.clientId}_${
    new Date().toISOString().split("T")[0]
  }.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return violationData;
};

// Function to log recent violations (useful for debugging from console)
window.logRecentViolations = (count = 5) => {
  const registry = state.violationRegistry || [];
  if (registry.length === 0) {
    console.log("[VIEWER] No violations recorded in this session");
    return;
  }

  console.log(
    `[VIEWER] Last ${Math.min(
      count,
      registry.length
    )} violations with chunk info:`
  );

  // Get the most recent violations first
  const recentViolations = [...registry].reverse().slice(0, count);

  recentViolations.forEach((chunkInfo, index) => {
    console.log(`Violation ${index + 1}/${recentViolations.length}:`);
    console.log(`  Chunk: ${chunkInfo.chunkNumber}`);
    console.log(`  Key: ${chunkInfo.key}`);
    console.log(`  Time: ${chunkInfo.startTime} to ${chunkInfo.endTime}`);
    console.log(`  Violations: ${chunkInfo.violations.length}`);

    // Log each violation in the chunk
    chunkInfo.violations.forEach((v, i) => {
      console.log(`    ${i + 1}. ${v.type} at ${v.timestamp}`);
      console.log(`       Details:`, v.details);
    });

    console.log("-------------------");
  });

  // Also log instructions for retrieving the full data
  console.log(
    "[VIEWER] To export all violation data, run: window.exportViolationsToJSON()"
  );

  return recentViolations;
};

//------------------------------------------------------------------------------
const KinesisWebRTC = view(({ onStreamStopped }) => {
  state.localView = useRef(null);
  state.remoteView = useRef(null);
  const [refsReady, setRefsReady] = useState(false);

  // Add useEffect to handle ref initialization
  useEffect(() => {
    if (state.localView?.current && state.remoteView?.current) {
      setRefsReady(true);
    }
  }, [state.localView?.current, state.remoteView?.current]);

  useEffect(() => {
    const initializeState = async () => {
      // Wait for window.appState to be available
      if (!window.appState) {
        console.log("[VIEWER] Waiting for window.appState...");
        return;
      }

      console.log(
        "[VIEWER] Initializing with window.appState:",
        window.appState
      );

      // If exam is already completed, don't initialize further
      if (window.appState.mode === "EXAM_COMPLETE") {
        console.log("[VIEWER] Exam is already completed, showing end screen");
        state.mode = "EXAM_COMPLETE";
        state.examEndReason = window.appState.examEndReason;
        state.isExamStarted = false;
        state.examStartTime = null;
        state.isInitialized = true;
        return;
      }

      // Set state values
      state.role = window.appState.role;
      state.channelName = window.appState.channelName?.replace(/\s+/g, "_");
      state.clientId = window.appState.clientId;
      state.startTime = window.appState.startTime;
      state.endTime = window.appState.endTime;
      state.isExamStarted = window.appState.isExamStarted || false;
      state.examStartTime = window.appState.examStartTime || null;
      state.universityId = window.appState.universityId; // Set universityId from window.appState

      // Only start checking if we have a channel name
      if (state.channelName) {
        console.log(
          "[VIEWER] Starting initial master status check for channel:",
          state.channelName
        );
        const isMasterConnected = await checkMasterStatus();
        state.isMasterConnected = isMasterConnected;
        console.log("[VIEWER] Initial master status:", isMasterConnected);
      } else {
        console.error("[VIEWER] No channel name available in window.appState");
        return;
      }

      // Mark as initialized after everything is set up
      state.isInitialized = true;

      console.log("[VIEWER] State initialized:", {
        isExamStarted: state.isExamStarted,
        examStartTime: state.examStartTime,
        mode: state.mode,
        verificationStatus: state.verificationStatus,
        universityId: state.universityId, // Log universityId
      });
    };

    initializeState();

    // Cleanup function
    return () => {
      if (state.playerIsStarted) {
        stopPlayer();
      }
    };
  }, []);

  // Show loading state until initialized
  if (!state.isInitialized) {
    return (
      <div className="initialization-message">
        <p>Initializing exam session...</p>
      </div>
    );
  }

  // If exam is completed, show end screen directly
  if (
    state.mode === "EXAM_COMPLETE" ||
    window.appState?.mode === "EXAM_COMPLETE"
  ) {
    console.log("[VIEWER] Showing exam end screen");
    return (
      <ExamEndScreen
        reason={state.examEndReason || window.appState?.examEndReason}
      />
    );
  }

  // Render the main component with video elements
  return (
    <div>
      <div style={{ display: "none" }}>
        <video ref={state.localView} autoPlay playsInline muted />
        <video ref={state.remoteView} autoPlay playsInline />
      </div>
      {state.playerIsStarted ? (
        <VideoPlayers onStreamStopped={onStreamStopped} />
      ) : (
        <ConnectionHandler />
      )}
    </div>
  );
});

//------------------------------------------------------------------------------
/**
 *  Configuration Parameters:
 *
 *  1. Access Key ( NOT TO CHANGE )
 *  2. Secret Key ( NOT TO CHANGE )
 *  3. Session Token (Optional)
 *  4. region ( NOT TO CHANGE )
 *  5. Channel Name [ Changes everytime ]
 *  6. ClientId ( not to change | we need to use studentId instead )
 *  7. NAT Traversal  STUN / TURN ( NOT TO CHANGE )
 *  8. Trickle ICE (NOT TO CHANGE)
 *  9. Resolution ( changable)
 *  10. Role ( Master | Viewer )
 */
const ConnectionHandler = view(() => {
  useEffect(() => {
    let statusInterval;

    // Only proceed if we have all required data
    if (!state.channelName) {
      console.log("[VIEWER] ConnectionHandler: Waiting for channel name...");
      return;
    }

    console.log(
      "[VIEWER] ConnectionHandler: Starting checks for channel:",
      state.channelName
    );

    // Do an immediate check
    checkMasterStatus().then((isMasterConnected) => {
      state.isMasterConnected = isMasterConnected;
      console.log(
        "[VIEWER] ConnectionHandler initial check:",
        isMasterConnected
      );
    });

    // Set up interval to check periodically regardless of current status
    statusInterval = setInterval(async () => {
      const isMasterConnected = await checkMasterStatus();
      state.isMasterConnected = isMasterConnected;
      console.log("[VIEWER] Periodic master status check:", isMasterConnected);
    }, CHECK_INTERVAL);

    return () => {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.channelName]); // Re-run when channel name changes

  if (!state.channelName) {
    return (
      <div className="connection-status">
        <h3>Waiting for connection details...</h3>
      </div>
    );
  }

  return (
    <WelcomeScreen
      state={state}
      startPlayer={startPlayer}
      stopPlayer={stopPlayer}
    />
  );
});

//------------------------------------------------------------------------------
// separate component for the message controls
const MessageControls = view(() => {
  const {
    messages = { broadcast: "", master: "", currentView: "broadcast" },
    currentView = "broadcast",
  } = state;

  const handleSendMessage = (message) => {
    state.messageToSend = message;
    sendMessage();
  };

  const handleViewChange = (view) => {
    state.messages = state.messages || {
      broadcast: "",
      master: "",
      currentView: "broadcast",
    };
    state.messages.currentView = view;
  };

  return (
    <div className="chat-widget-container">
      <ChatWidget
        messages={messages}
        onSendMessage={handleSendMessage}
        currentView={currentView}
        onViewChange={handleViewChange}
      />
    </div>
  );
});

// Main VideoPlayers component
const VideoPlayers = view(({ onStreamStopped }) => {
  const {
    role,
    localView,
    clientId,
    verificationStatus,
    isExamStarted,
    examStartTime,
  } = state;

  // State hooks
  const [faceDetectionComplete, setFaceDetectionComplete] = useState(false);
  const [faceDetectionStarted, setFaceDetectionStarted] = useState(false);
  const [confidence, setConfidence] = useState(0);
  const [roomScanComplete, setRoomScanComplete] = useState(false);
  const [roomScanStarted, setRoomScanStarted] = useState(false);
  const [isRoomScanDialogOpen, setIsRoomScanDialogOpen] = useState(false);
  const [isClarificationDialogOpen, setIsClarificationDialogOpen] =
    useState(false);
  const [isIdCardDialogOpen, setIsIdCardDialogOpen] = useState(false);
  const [idCardVerified, setIdCardVerified] = useState(false);
  const [detectedIssue, setDetectedIssue] = useState(null);
  const roomScanTimeoutRef = useRef(null);
  const objectDetectionRef = useRef(null);
  const showDialogTimerRef = useRef(null);
  const closeDialogTimerRef = useRef(null);

  // Add effect to sync verification states with global state
  useEffect(() => {
    state.faceDetectionComplete = faceDetectionComplete;
    state.roomScanComplete = roomScanComplete;
    state.idCardVerified = idCardVerified;

    // Log state changes
    console.log("[VIEWER] Verification states updated:", {
      face: faceDetectionComplete,
      room: roomScanComplete,
      idCard: idCardVerified,
      proctor: state.proctorVerified,
      mode: state.mode,
    });

    // Check if all verifications are complete
    if (
      faceDetectionComplete &&
      roomScanComplete &&
      idCardVerified &&
      state.proctorVerified
    ) {
      console.log("[VIEWER] All verifications complete");
      state.verificationStatus = "VERIFIED";

      // If we're in exam mode but not started, start the exam
      if (state.mode === "EXAM" && !state.isExamStarted) {
        console.log("[VIEWER] Starting exam");
        state.isExamStarted = true;
        state.examStartTime = new Date().toISOString();
        window.appState = {
          ...window.appState,
          isExamStarted: true,
          examStartTime: state.examStartTime,
          mode: "EXAM",
        };
      }
    }
  }, [
    faceDetectionComplete,
    roomScanComplete,
    idCardVerified,
    state.proctorVerified,
    state.mode,
  ]);

  // Add state reset effect when component mounts or data channel changes
  useEffect(() => {
    // Only reset states if in verification mode and exam hasn't started
    if (state.mode === "VERIFICATION" && !state.isExamStarted) {
      console.log("[VIEWER] Resetting verification states on reconnect");
      setFaceDetectionStarted(false);
      setFaceDetectionComplete(false);
      setConfidence(0);
      setRoomScanStarted(false);
      setRoomScanComplete(false);
      setIdCardVerified(false);
      setIsIdCardDialogOpen(false);
      setDetectedIssue(null);
      setIsClarificationDialogOpen(false);
      setIsRoomScanDialogOpen(false);

      // Clean up any existing handlers
      if (objectDetectionRef.current) {
        objectDetectionRef.current.cleanup();
        objectDetectionRef.current = null;
      }
    }
  }, [state.dataChannel, state.mode, state.isExamStarted]);

  // Update face detection handler to check stored state
  useEffect(() => {
    const handleFaceDetectionResult = (confidence) => {
      // Only update if not already completed
      if (!faceDetectionComplete) {
        const isComplete = confidence >= 80;
        setFaceDetectionComplete(isComplete);
        setFaceDetectionStarted(true);
        setConfidence(confidence);
      }
    };

    // Listen for face comparison results
    if (state.dataChannel?.readyState === "open") {
      state.dataChannel.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "FACE_COMPARISON_RESULT") {
            handleFaceDetectionResult(data.confidence);
          }
        } catch (error) {
          console.error("[VIEWER] Error parsing message:", error);
        }
      });
    }
  }, [state.dataChannel, faceDetectionComplete]);

  const stopRoomScan = useCallback(() => {
    console.log("[VIEWER] Stopping room scan");
    if (roomScanTimeoutRef.current) {
      clearTimeout(roomScanTimeoutRef.current);
      roomScanTimeoutRef.current = null;
    }
    if (objectDetectionRef.current) {
      objectDetectionRef.current.stopRoomScan();
    }
    setRoomScanStarted(false);
  }, []);

  const handleDetection = useCallback(
    (detection) => {
      console.log(
        "[VIEWER] Detection during room scan:",
        detection,
        state.mode,
        state.isExamStarted
      );

      // Only handle detections during verification mode and when exam hasn't started
      if (
        state.mode === "VERIFICATION" &&
        !state.isExamStarted &&
        detection.type === "multiple_faces"
      ) {
        console.log(
          "[VIEWER] Showing clarification dialog for multiple faces:",
          detection
        );

        // First update the detected issue
        setDetectedIssue({
          type: detection.type,
          details: detection.details,
        });

        // Then show the clarification dialog and hide room scan dialog
        setIsClarificationDialogOpen(true);
        setIsRoomScanDialogOpen(false);

        // Pause the room scan but don't complete it
        setRoomScanStarted(false);
        stopRoomScan();

        console.log("[VIEWER] States updated:", {
          detectedIssue: {
            type: detection.type,
            details: detection.details,
          },
          isClarificationDialogOpen: true,
          isRoomScanDialogOpen: false,
          roomScanStarted: false,
          roomScanComplete: false,
          isExamStarted: state.isExamStarted,
        });
      }
    },
    [stopRoomScan]
  );

  // room scan completion handler
  const handleRoomScanComplete = useCallback(() => {
    console.log("[VIEWER] Room scan completed");
    stopRoomScan();
    setRoomScanComplete(true);
    setRoomScanStarted(false);

    // Cleanup object detection handler
    if (objectDetectionRef.current) {
      objectDetectionRef.current.cleanup();
      objectDetectionRef.current = null;
    }

    // Send room scan completion message to proctor
    if (state.dataChannel?.readyState === "open") {
      const message = {
        type: "ROOM_SCAN_COMPLETE",
        studentId: clientId,
        timestamp: new Date().toISOString(),
        status: "completed",
        duration: "20s",
      };
      state.dataChannel.send(JSON.stringify(message));
      console.log("[VIEWER] Room scan completion sent to proctor:", message);
    }
  }, [state.dataChannel, clientId, stopRoomScan]);

  // Add cleanup effect for object detection
  useEffect(() => {
    return () => {
      if (objectDetectionRef.current) {
        objectDetectionRef.current.cleanup();
        objectDetectionRef.current = null;
      }
    };
  }, []);

  // Update startRoomScan to respect completion state
  const startRoomScan = useCallback(() => {
    // Don't start if already completed
    if (roomScanComplete) {
      console.log("[VIEWER] Room scan already completed, skipping");
      return;
    }

    console.log("[VIEWER] Starting room scan");
    setRoomScanStarted(true);
    setIsRoomScanDialogOpen(false);

    // Initialize object detection if not already done
    if (!objectDetectionRef.current) {
      objectDetectionRef.current = new ObjectDetectionHandler(
        state.dataChannel,
        clientId,
        handleDetection
      );
    } else {
      objectDetectionRef.current.onDetection = handleDetection;
    }

    objectDetectionRef.current.startRoomScan();

    if (roomScanTimeoutRef.current) {
      clearTimeout(roomScanTimeoutRef.current);
    }

    roomScanTimeoutRef.current = setTimeout(handleRoomScanComplete, 20000); //20 seconds
  }, [
    state.dataChannel,
    clientId,
    handleDetection,
    roomScanComplete,
    handleRoomScanComplete,
  ]);

  // Add effect to check verification status on reconnection
  useEffect(() => {
    if (state.dataChannel?.readyState === "open" && !state.isExamStarted) {
      // Send verification status check request to proctor
      const message = {
        type: "VERIFICATION_STATUS_CHECK",
        studentId: clientId,
        faceVerified: faceDetectionComplete,
        roomVerified: roomScanComplete,
        timestamp: new Date().toISOString(),
      };
      state.dataChannel.send(JSON.stringify(message));
      console.log("[VIEWER] Sent verification status check:", message);
    }
  }, [
    state.dataChannel?.readyState,
    state.isExamStarted,
    clientId,
    faceDetectionComplete,
    roomScanComplete,
  ]);

  useEffect(() => {
    const handleExamStateChange = (event) => {
      console.log("[VIEWER] Received examStateChange event:", event.detail);
      const { mode, reason, hasOfflineAnswers } = event.detail;

      // Update state based on the event
      if (mode === "ANSWER_UPLOAD" && hasOfflineAnswers) {
        console.log("[VIEWER] Transitioning to answer upload mode");
        state.mode = "ANSWER_UPLOAD";
        state.isExamStarted = false;
      } else if (mode === "EXAM_COMPLETE") {
        console.log("[VIEWER] Transitioning to exam complete mode");
        state.mode = "EXAM_COMPLETE";
        state.examEndReason = reason;
        state.isExamStarted = false;
      }

      console.log("[VIEWER] Updated state after event:", {
        mode: state.mode,
        isExamStarted: state.isExamStarted,
        examEndReason: state.examEndReason,
      });
    };

    console.log("[VIEWER] Setting up examStateChange event listener");
    window.addEventListener("examStateChange", handleExamStateChange);
    return () => {
      console.log("[VIEWER] Cleaning up examStateChange event listener");
      window.removeEventListener("examStateChange", handleExamStateChange);
    };
  }, []);

  useEffect(() => {
    const handleFaceDetectionResult = (confidence) => {
      setFaceDetectionComplete(confidence >= 80);
      setFaceDetectionStarted(true);
      setConfidence(confidence);
    };

    // Listen for face comparison results
    if (state.dataChannel?.readyState === "open") {
      state.dataChannel.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "FACE_COMPARISON_RESULT") {
            handleFaceDetectionResult(data.confidence);
          }
        } catch (error) {
          console.error("[VIEWER] Error parsing message:", error);
        }
      });
    }
  }, [state.dataChannel]);

  // Add function to compare faces using AWS Rekognition
  const compareFaces = async () => {
    try {
      // Fetch photo ID details
      const photoIdResponse = await fetch(
        `https://procbackend.elcarreira.com/students/getPhotoId?studentId=${clientId}`
      );
      const photoIdData = await photoIdResponse.json();

      // Fetch screenshot details
      const screenshotResponse = await fetch(
        `https://procbackend.elcarreira.com/students/getScreenshot?studentId=${clientId}&examId=${window.appState.examId}`
      );
      const screenshotData = await screenshotResponse.json();

      console.log("[VIEWER] Fetched image details:", {
        photoIdData,
        screenshotData,
      });

      // Create S3 client
      const s3 = new AWS.S3({
        region: config.aws.region,
        accessKeyId: config.aws.credentials.accessKeyId,
        secretAccessKey: config.aws.credentials.secretAccessKey,
      });

      // Get screenshot image from S3
      const screenshotImage = await s3
        .getObject({
          Bucket: screenshotData.bucketName,
          Key: screenshotData.key,
        })
        .promise();

      try {
        // Get photo ID image from S3
        const photoIdImage = await s3
          .getObject({
            Bucket: photoIdData.bucketName,
            Key: photoIdData.key,
          })
          .promise();

        // Create Rekognition client
        const rekognition = new AWS.Rekognition({
          region: config.aws.region,
          accessKeyId: config.aws.credentials.accessKeyId,
          secretAccessKey: config.aws.credentials.secretAccessKey,
        });

        // Compare faces
        const compareResult = await rekognition
          .compareFaces({
            SourceImage: {
              Bytes: photoIdImage.Body,
            },
            TargetImage: {
              Bytes: screenshotImage.Body,
            },
            SimilarityThreshold: 80,
          })
          .promise();

        if (compareResult.FaceMatches && compareResult.FaceMatches.length > 0) {
          const confidence = compareResult.FaceMatches[0].Similarity;
          console.log("[VIEWER] Face comparison confidence:", confidence);

          // Send result to proctor
          if (state.dataChannel?.readyState === "open") {
            const message = {
              type: "FACE_COMPARISON_RESULT",
              studentId: clientId,
              confidence: confidence,
              result: confidence >= 80 ? "MATCH" : "NO_MATCH",
              status:
                confidence >= 80
                  ? "Face ID verification successful"
                  : "Face ID verification failed - Low confidence match",
              timestamp: new Date().toISOString(),
            };
            state.dataChannel.send(JSON.stringify(message));
            console.log(
              "[VIEWER] Face comparison result sent to proctor:",
              message
            );

            // Update face detection states immediately after sending
            setFaceDetectionStarted(true);
            setFaceDetectionComplete(confidence >= 80);
            setConfidence(confidence);
          }

          return confidence;
        } else {
          console.log("[VIEWER] No matching faces found");

          // Send no match result to proctor
          if (state.dataChannel?.readyState === "open") {
            const message = {
              type: "FACE_COMPARISON_RESULT",
              studentId: clientId,
              confidence: 0,
              result: "NO_MATCH",
              status: "Face ID verification failed - No face match found",
              timestamp: new Date().toISOString(),
            };
            state.dataChannel.send(JSON.stringify(message));
            console.log(
              "[VIEWER] Face comparison result sent to proctor:",
              message
            );

            // Update face detection states for no match
            setFaceDetectionStarted(true);
            setFaceDetectionComplete(false);
            setConfidence(0);
            setIdCardVerified(true);
            setIsIdCardDialogOpen(false);
          }

          return 0;
        }
      } catch (error) {
        console.log("[VIEWER] Error with photo ID or comparison:", error);

        // Send error result to proctor
        if (state.dataChannel?.readyState === "open") {
          const message = {
            type: "FACE_COMPARISON_RESULT",
            studentId: clientId,
            confidence: 0,
            result: "ERROR",
            status: "Face ID verification failed - Error during comparison",
            error: error.message,
            timestamp: new Date().toISOString(),
          };
          state.dataChannel.send(JSON.stringify(message));
          console.log(
            "[VIEWER] Face comparison error sent to proctor:",
            message
          );
        }

        return 0;
      }
    } catch (error) {
      console.error("[VIEWER] Error in face comparison:", error);

      // Send error result to proctor
      if (state.dataChannel?.readyState === "open") {
        const message = {
          type: "FACE_COMPARISON_RESULT",
          studentId: clientId,
          confidence: 0,
          result: "ERROR",
          status: "Face ID verification failed - Error fetching images",
          error: error.message,
          timestamp: new Date().toISOString(),
        };
        state.dataChannel.send(JSON.stringify(message));
        console.log("[VIEWER] Face comparison error sent to proctor:", message);
      }

      return 0;
    }
  };

  // Add function to capture and upload screenshot
  const captureAndUploadScreenshot = async () => {
    if (!localView.current) {
      console.log("[VIEWER] Video element not available");
      return;
    }

    // Wait for video to be properly loaded
    if (!localView.current.videoWidth || !localView.current.videoHeight) {
      console.log("[VIEWER] Waiting for video dimensions to be available...");
      await new Promise((resolve) => {
        localView.current.addEventListener(
          "loadeddata",
          () => {
            // Add a small delay to ensure stream is stable
            setTimeout(resolve, 1000);
          },
          { once: true }
        );
      });
    }

    console.log("[VIEWER] Video dimensions:", {
      width: localView.current.videoWidth,
      height: localView.current.videoHeight,
    });

    try {
      // Create a canvas element
      const canvas = document.createElement("canvas");
      canvas.width = localView.current.videoWidth;
      canvas.height = localView.current.videoHeight;

      // Draw the current video frame to canvas
      const ctx = canvas.getContext("2d");
      ctx.drawImage(localView.current, 0, 0);

      // Convert canvas to blob
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.95)
      );

      console.log("[VIEWER] Screenshot captured, preparing to upload...");

      // Create form data
      const formData = new FormData();
      formData.append("file", blob, "verification.jpg");

      // Send to API
      const response = await fetch(
        `https://procbackend.elcarreira.com/students/uploadScreenshot?studentId=${clientId}&examId=${window.appState.examId}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload screenshot");
      }

      console.log("[VIEWER] Verification screenshot uploaded successfully");

      // After successful upload, compare faces
      const confidence = await compareFaces();
      console.log(
        "[VIEWER] Face comparison completed with confidence:",
        confidence
      );
    } catch (error) {
      console.error(
        "[VIEWER] Failed to upload verification screenshot:",
        error
      );
    }
  };

  // Add useEffect to show ID card dialog when verification starts
  useEffect(() => {
    // Only proceed if we're in verification mode and not already verified
    if (
      state.mode === "VERIFICATION" &&
      verificationStatus === "PENDING" &&
      !idCardVerified
    ) {
      console.log(
        "[VIEWER] In verification mode, preparing to show ID card dialog"
      );

      // Set a fixed timeout for showing the dialog
      const showTimer = setTimeout(() => {
        console.log("[VIEWER] Showing ID card dialog");
        setIsIdCardDialogOpen(true);
      }, 5000);

      // Return cleanup function
      return () => {
        clearTimeout(showTimer);
      };
    }
  }, [state.mode, verificationStatus, idCardVerified]);

  // Separate effect to handle auto-closing of the dialog
  useEffect(() => {
    // Only set up auto-close timer if dialog is open
    if (isIdCardDialogOpen) {
      console.log("[VIEWER] ID card dialog opened, setting auto-close timer");

      // Set a fixed timeout for auto-closing
      const closeTimer = setTimeout(() => {
        console.log("[VIEWER] Auto-closing ID card dialog after 8 seconds");
        setIsIdCardDialogOpen(false);
        setIdCardVerified(true);

        // Start face verification after dialog closes
        setTimeout(() => {
          captureAndUploadScreenshot();
        }, 500);
      }, 8000);

      // Return cleanup function
      return () => {
        clearTimeout(closeTimer);
      };
    }
  }, [isIdCardDialogOpen]);

  // Handle ID card verification continuation (for manual close)
  const handleIdCardContinue = () => {
    console.log("[VIEWER] User confirmed ID card is ready");

    setIsIdCardDialogOpen(true);
    setIdCardVerified(true);

    // Now proceed with screenshot and face verification
    console.log("[VIEWER] Starting face verification after ID card...");
    setTimeout(() => {
      captureAndUploadScreenshot();
    }, 10000);
  };

  // Add useEffect to capture screenshot in verification stage - modified to wait for ID card verification
  useEffect(() => {
    if (
      state.mode === "VERIFICATION" &&
      verificationStatus === "PENDING" &&
      idCardVerified
    ) {
      console.log(
        "[VIEWER] ID card verified, continuing verification process..."
      );
      setIdCardVerified(true);
      // We no longer need this as handleIdCardContinue will trigger the screenshot capture
      // The face verification will start automatically after ID card verification
    }
  }, [state.mode, verificationStatus, idCardVerified]);

  // Add effect to handle room scan after face detection
  useEffect(() => {
    if (
      faceDetectionComplete &&
      !roomScanStarted &&
      !roomScanComplete &&
      !isClarificationDialogOpen // Don't show room scan dialog if clarification dialog is open
    ) {
      console.log(
        "[VIEWER] Face detection complete, waiting 3 seconds before room scan..."
      );
      const timer = setTimeout(() => {
        console.log("[VIEWER] Showing room scan dialog");
        setIsRoomScanDialogOpen(true);
      }, 3000); // 3 second delay

      return () => clearTimeout(timer);
    }
  }, [
    faceDetectionComplete,
    roomScanStarted,
    roomScanComplete,
    isClarificationDialogOpen,
  ]);

  // Add effect for video frame processing
  useEffect(() => {
    if (
      !roomScanStarted ||
      roomScanComplete ||
      !localView.current ||
      !objectDetectionRef.current
    ) {
      return;
    }

    console.log("[VIEWER] Starting video frame processing");
    const videoElement = localView.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    const processFrame = async () => {
      if (videoElement.videoWidth && videoElement.videoHeight) {
        // Set canvas size to match video
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        // Draw current video frame to canvas
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        // Get frame data as blob
        try {
          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.7)
          );
          const arrayBuffer = await blob.arrayBuffer();
          const imageData = new Uint8Array(arrayBuffer);

          // Process frame with object detection
          if (objectDetectionRef.current) {
            await objectDetectionRef.current.processImage(imageData);
          }
        } catch (error) {
          console.error("[VIEWER] Error processing video frame:", error);
        }
      }

      // Schedule next frame if still scanning
      if (roomScanStarted && !roomScanComplete) {
        animationFrameId = requestAnimationFrame(processFrame);
      }
    };

    // Start processing frames
    animationFrameId = requestAnimationFrame(processFrame);

    // Cleanup
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [roomScanStarted, roomScanComplete, localView]);

  const handleClarificationSubmit = useCallback(
    (reason) => {
      console.log("[VIEWER] Clarification submitted:", reason);
      setIsClarificationDialogOpen(false);
      setDetectedIssue(null);

      // Send clarification to proctor
      if (state.dataChannel?.readyState === "open") {
        const message = {
          type: "room_scan_clarification",
          studentId: clientId,
          issue: detectedIssue,
          clarification: reason,
          timestamp: new Date().toISOString(),
        };
        state.dataChannel.send(JSON.stringify(message));
      }

      // Mark room scan as complete instead of resuming
      setRoomScanComplete(true);
      setRoomScanStarted(false);
      stopRoomScan();
    },
    [state.dataChannel, clientId, detectedIssue, stopRoomScan]
  );

  // Modify the existing verificationSteps array
  const verificationSteps = [
    {
      title: "Face Detection",
      description: faceDetectionStarted
        ? faceDetectionComplete
          ? "Face verification successful (" +
            Math.round(confidence) +
            "% match)"
          : "Face verification failed - Low confidence match"
        : "System is verifying your face...",
      icon: <PiUserCircleBold />,
      status: !faceDetectionStarted
        ? "pending"
        : faceDetectionComplete
        ? "completed"
        : "failed",
      color: !faceDetectionStarted
        ? "#95a5a6"
        : faceDetectionComplete
        ? "#2ecc71"
        : "#e74c3c",
      animation: !faceDetectionStarted && "pulse",
    },
    {
      title: "Room Verification",
      description: roomScanComplete
        ? "Room verification successful"
        : roomScanStarted
        ? "Scanning room..."
        : faceDetectionComplete
        ? "Click to start room scan"
        : "Waiting for face verification",
      icon: <MdMeetingRoom />,
      status: roomScanComplete
        ? "completed"
        : roomScanStarted
        ? "current"
        : "pending",
      color: roomScanComplete
        ? "#2ecc71"
        : roomScanStarted
        ? "#3498db"
        : "#95a5a6",
      animation: roomScanStarted && !roomScanComplete && "pulse",
    },
    {
      title: "Proctor Review",
      description: state.proctorVerified
        ? "Proctor has approved your verification"
        : roomScanComplete && faceDetectionComplete
        ? "Waiting for proctor verification"
        : "Complete previous steps",
      icon: <BsFillPersonCheckFill />,
      status: state.proctorVerified
        ? "completed"
        : roomScanComplete && faceDetectionComplete
        ? "current"
        : "pending",
      color: state.proctorVerified
        ? "#2ecc71"
        : roomScanComplete && faceDetectionComplete
        ? "#3498db"
        : "#95a5a6",
      animation:
        roomScanComplete &&
        faceDetectionComplete &&
        !state.proctorVerified &&
        "pulse",
    },
  ];

  // Handle session end
  const handleSessionEnd = useCallback(async (reason) => {
    console.log("[VIEWER] Ending session, reason:", reason);

    // If reason is MAX_WARNINGS, call saveResponses API first, then check for offline answers
    if (reason === "MAX_WARNINGS") {
      try {
        // First save any existing answers
        const savedData = localStorage.getItem("examAnswers");
        if (savedData) {
          console.log("[VIEWER] Submitting final answers before termination");
          const saveResponse = await fetch(
            "https://procbackend.elcarreira.com/exam/saveResponses",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: savedData,
            }
          );

          if (!saveResponse.ok) {
            console.error("[VIEWER] Failed to save answers before termination");
          } else {
            console.log(
              "[VIEWER] Successfully saved answers before termination"
            );
          }
        }

        // Check if there are offline answers that need to be uploaded
        const hasOfflineAnswers =
          savedData && JSON.parse(savedData)?.offlineAnswers?.length > 0;
        console.log("[VIEWER] Offline answers check:", { hasOfflineAnswers });

        // Then call terminate API
        const terminateResponse = await fetch(
          `https://procbackend.elcarreira.com/proctor/terminateStudent?studentId=${state.clientId}&examId=${window.appState.examId}`
        );

        if (!terminateResponse.ok) {
          console.error("[VIEWER] Failed to register termination");
        } else {
          console.log("[VIEWER] Student termination registered successfully");
        }

        // Clean up resources first
        if (state.localStream) {
          state.localStream.getTracks().forEach((track) => track.stop());
        }

        if (state.peerConnectionStatsInterval) {
          clearInterval(state.peerConnectionStatsInterval);
          state.peerConnectionStatsInterval = null;
        }

        if (state.signalingClient) {
          state.signalingClient.close();
          state.signalingClient = null;
        }

        if (state.peerConnection) {
          state.peerConnection.close();
          state.peerConnection = null;
        }

        // Set appropriate mode based on offline answers
        if (hasOfflineAnswers) {
          console.log(
            "[VIEWER] Has offline answers, transitioning to upload mode"
          );
          state.mode = "ANSWER_UPLOAD";
          window.appState = {
            ...window.appState,
            mode: "ANSWER_UPLOAD",
            examEndReason: reason,
            isExamStarted: false,
          };
        } else {
          console.log(
            "[VIEWER] No offline answers, transitioning to exam complete"
          );
          state.mode = "EXAM_COMPLETE";
          window.appState = {
            ...window.appState,
            mode: "EXAM_COMPLETE",
            examEndReason: reason,
            isExamStarted: false,
          };
        }

        // Stop the stream
        if (typeof stopPlayer === "function") {
          stopPlayer();
        }

        // Clear localStorage after determining offline answers
        try {
          localStorage.removeItem("suspiciousActions");
          if (!hasOfflineAnswers) {
            localStorage.removeItem("examAnswers");
          }
          console.log("[VIEWER] Successfully cleared localStorage");
        } catch (error) {
          console.error("[VIEWER] Error clearing localStorage:", error);
        }

        // Dispatch state change event
        window.dispatchEvent(
          new CustomEvent("examStateChange", {
            detail: {
              mode: hasOfflineAnswers ? "ANSWER_UPLOAD" : "EXAM_COMPLETE",
              reason,
              hasOfflineAnswers,
              timestamp: new Date().toISOString(),
            },
          })
        );
      } catch (error) {
        console.error("[VIEWER] Error in termination process:", error);
        // Even on error, try to cleanup
        state.mode = "EXAM_COMPLETE";
        window.appState = {
          ...window.appState,
          mode: "EXAM_COMPLETE",
          examEndReason: "ERROR",
          isExamStarted: false,
        };
      }
      return; // Exit early after handling MAX_WARNINGS case
    }

    // Rest of the existing handleSessionEnd code for other reasons...

    // Send signal to proctor
    if (state.dataChannel?.readyState === "open") {
      try {
        state.dataChannel.send(
          JSON.stringify({
            type: "UPLOAD_PHASE_END",
            status: reason,
            studentId: state.clientId,
            timestamp: new Date().toISOString(),
          })
        );
      } catch (error) {
        console.error("[VIEWER] Failed to send end signal:", error);
      }
    }

    // Rest of the existing cleanup code...
    if (state.localStream) {
      state.localStream.getTracks().forEach((track) => {
        track.stop();
      });
    }

    if (state.peerConnectionStatsInterval) {
      clearInterval(state.peerConnectionStatsInterval);
      state.peerConnectionStatsInterval = null;
    }

    if (state.signalingClient) {
      state.signalingClient.close();
      state.signalingClient = null;
    }

    if (state.peerConnection) {
      state.peerConnection.close();
      state.peerConnection = null;
    }

    if (state.dataChannel) {
      state.dataChannel.close();
      state.dataChannel = null;
    }

    if (state.localView?.current) {
      state.localView.current.srcObject = null;
    }
    if (state.remoteView?.current) {
      state.remoteView.current.srcObject = null;
    }

    // Clear localStorage only after saving answers
    try {
      localStorage.removeItem("suspiciousActions");
      if (reason === "MAX_WARNINGS") {
        localStorage.removeItem("examAnswers");
      }
      console.log("[VIEWER] Successfully cleared localStorage");
    } catch (error) {
      console.error("[VIEWER] Error clearing localStorage:", error);
    }

    // Update appState but preserve necessary information
    const currentState = window.appState || {};
    window.appState = {
      ...currentState,
      examEndReason: reason,
      mode: "EXAM_COMPLETE",
      isExamStarted: false,
      examStartTime: null,
    };
    console.log("[VIEWER] Final appState:", window.appState);
  }, []);

  useEffect(() => {
    console.log("[VIEWER] VideoPlayers state update:", {
      verificationStatus,
      isExamStarted,
      examStartTime,
    });
  }, [verificationStatus, isExamStarted, examStartTime]);

  const handleStopStream = () => {
    // If called from FaceDetectionWrapper's onMaxWarnings
    if (state.behaviorAlerts.length >= 3) {
      handleSessionEnd("MAX_WARNINGS");
    } else {
      stopPlayer();
      if (onStreamStopped) {
        onStreamStopped();
      }
    }
  };

  const renderVerificationStatus = () => {
    console.log(
      "[VIEWER] Rendering verification status:",
      verificationStatus,
      state.mode
    );

    const getStatusIcon = () => {
      switch (verificationStatus) {
        case "PENDING":
          return <FaSpinner className="spinning" />;
        case "VERIFIED":
          return <FaCheckCircle />;
        case "REJECTED":
          return <FaTimesCircle />;
        default:
          return <FaSpinner />;
      }
    };

    const getStatusColor = () => {
      switch (verificationStatus) {
        case "PENDING":
          return "#3498db"; // Modern blue
        case "VERIFIED":
          return "#2ecc71"; // Modern green
        case "REJECTED":
          return "#e74c3c"; // Modern red
        default:
          return "#95a5a6"; // Modern grey
      }
    };

    return (
      <div
        className="verification-status-container"
        style={{
          padding: "25px",
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        <div
          className="verification-header"
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "25px",
            padding: "15px",
            background: `linear-gradient(135deg, ${getStatusColor()} 0%, ${getStatusColor()}dd 100%)`,
            borderRadius: "10px",
            color: "#fff",
            transition: "all 0.3s ease",
          }}
        >
          <span
            style={{
              fontSize: "28px",
              marginRight: "15px",
              animation:
                verificationStatus === "PENDING"
                  ? "spin 2s linear infinite"
                  : "none",
            }}
          >
            {getStatusIcon()}
          </span>
          <div>
            <h3
              style={{
                margin: "0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              {verificationStatus === "PENDING"
                ? "Verification in Progress"
                : verificationStatus === "VERIFIED"
                ? "Verification Complete"
                : "Verification Failed"}
            </h3>
            <p
              style={{
                margin: "5px 0 0 0",
                fontSize: "14px",
                opacity: "0.9",
              }}
            >
              {verificationStatus === "PENDING"
                ? "Please wait while we verify your identity"
                : verificationStatus === "VERIFIED"
                ? "You can proceed with the exam"
                : "Please contact the administrator"}
            </p>
          </div>
        </div>

        <div
          className="verification-steps"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "15px",
          }}
        >
          {verificationSteps.map((step, index) => (
            <div
              key={index}
              className="step"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "20px",
                backgroundColor: step.status === "current" ? "#f8f9fa" : "#fff",
                borderRadius: "10px",
                border: `1px solid ${step.color}40`,
                transition: "all 0.3s ease",
                transform:
                  step.status === "current" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  step.status === "current"
                    ? "0 2px 4px rgba(0,0,0,0.05)"
                    : "none",
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  display: "flex",
                  color: "#fff",
                  fontSize: "20px",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: step.color,
                  borderRadius: "50%",
                  marginRight: "15px",
                  transition: "all 0.3s ease",
                  animation:
                    step.animation === "pulse" ? "pulse 2s infinite" : "none",
                }}
              >
                {step.status === "completed" ? (
                  <PiUserCircleCheckBold />
                ) : step.status === "failed" ? (
                  <PiUserCircleMinusBold />
                ) : (
                  step.icon
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h4
                  style={{
                    margin: "0",
                    color: "#2c3e50",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  {step.title}
                </h4>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    color: step.status === "failed" ? "#e74c3c" : "#7f8c8d",
                    fontSize: "14px",
                  }}
                >
                  {step.description}
                </p>
              </div>
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: step.color,
                }}
              >
                {step.status === "completed" ? (
                  <FaCheckCircle />
                ) : step.status === "failed" ? (
                  <FaTimesCircle />
                ) : step.status === "current" ? (
                  "●"
                ) : (
                  "○"
                )}
              </div>
            </div>
          ))}
        </div>

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.05); }
              100% { transform: scale(1); }
            }
            .spinning {
              animation: spin 2s linear infinite;
            }
          `}
        </style>

        {verificationStatus === "PENDING" && (
          <div
            className="verification-tips"
            style={{
              marginTop: "25px",
              padding: "20px",
              backgroundColor: "#f8f9fa",
              borderRadius: "10px",
              border: "1px solid #e9ecef",
            }}
          >
            <h4
              style={{
                margin: "0 0 12px 0",
                color: "#2c3e50",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FaLightbulb style={{ color: "#3498db" }} /> Verification Tips
            </h4>
            <ul
              style={{
                margin: "0",
                paddingLeft: "20px",
                color: "#7f8c8d",
                listStyle: "none",
              }}
            >
              <li
                style={{
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaCheckCircle style={{ color: "#3498db", fontSize: "14px" }} />
                Ensure proper lighting on your face
              </li>
              <li
                style={{
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaCheckCircle style={{ color: "#3498db", fontSize: "14px" }} />
                Remove any face coverings or sunglasses
              </li>
              <li
                style={{
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaCheckCircle style={{ color: "#3498db", fontSize: "14px" }} />
                Look directly at the camera
              </li>
            </ul>
          </div>
        )}

        {verificationStatus === "REJECTED" && (
          <div
            className="rejection-message"
            style={{
              marginTop: "25px",
              padding: "20px",
              backgroundColor: "#fdf1f0",
              borderRadius: "10px",
              border: "1px solid #fadbd8",
              color: "#c0392b",
            }}
          >
            <h4
              style={{
                margin: "0 0 12px 0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <BsExclamationTriangle style={{ fontSize: "20px" }} />{" "}
              Verification Failed
            </h4>
            <p style={{ margin: "0" }}>
              Your verification was unsuccessful. Please ensure:
              <ul
                style={{
                  marginTop: "12px",
                  paddingLeft: "20px",
                }}
              >
                <li
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <FaTimesCircle
                    style={{ color: "#e74c3c", fontSize: "14px" }}
                  />
                  Your face is clearly visible
                </li>
                <li
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <FaTimesCircle
                    style={{ color: "#e74c3c", fontSize: "14px" }}
                  />
                  The lighting is adequate
                </li>
                <li
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <FaTimesCircle
                    style={{ color: "#e74c3c", fontSize: "14px" }}
                  />
                  You're looking directly at the camera
                </li>
              </ul>
              Contact support if you continue to have issues.
            </p>
          </div>
        )}
      </div>
    );
  };

  // If exam is completed, show end screen
  if (state.mode === "EXAM_COMPLETE") {
    console.log("[VIEWER] Rendering exam end screen");
    return <ExamEndScreen reason={state.examEndReason} />;
  }

  return (
    <div className="exam-container">
      <div className="question-paper-section">
        {state.isExamStarted && state.mode === "EXAM" ? (
          <QuestionPaper examId={window.appState.examId} />
        ) : (
          <>
            <div className="question-paper-header">
              <h2>Verification Status</h2>
            </div>
            <div className="question-paper-content">
              {renderVerificationStatus()}
            </div>
          </>
        )}
      </div>

      <div className="exam-session-section">
        <div className="exam-session-header">
          {state.isExamStarted && state.mode === "EXAM" && (
            <ExamTimer
              endTime={state.endTime}
              onExamComplete={() => {
                console.log(
                  "[VIEWER] Exam completed, switching to answer upload mode"
                );
                state.mode = "ANSWER_UPLOAD";
                state.isExamStarted = false;
                state.forceUpdate = Date.now();
              }}
            />
          )}
        </div>

        <div className="video-streams">
          {/* Student Video */}
          <div className="video-container student">
            <video ref={state.localView} autoPlay playsInline muted />
            {role === OPTIONS.ROLE.VIEWER &&
              state.localView.current &&
              state.mode === "EXAM" &&
              state.isExamStarted && (
                <FaceDetectionWrapper
                  key={`face-detection-${state.mode}`}
                  videoStream={state.localView.current}
                  dataChannel={state.dataChannel}
                  viewerId={state.clientId}
                  onMaxWarnings={() => handleSessionEnd("MAX_WARNINGS")}
                />
              )}
            <div className="video-label">
              <span className="status-indicator"></span>
              Student (You)
            </div>
          </div>

          {/* Proctor Video and Instructions */}
          <div className="proctor-section">
            <div className="video-container proctor">
              <video ref={state.remoteView} autoPlay playsInline />
              <div className="video-label">
                <span className="status-indicator"></span>
                Proctor
              </div>
            </div>

            {/* Only show instructions during exam mode */}
            {state.mode === "EXAM" && (
              <div className="instructions-panel">
                <h4>Exam Instructions</h4>
                <ul>
                  <li>Keep your face clearly visible in the camera frame</li>
                  <li>Ensure proper lighting in your room</li>
                  <li>Do not wear headphones or earbuds</li>
                  <li>Keep your microphone unmuted</li>
                  <li>Do not use virtual backgrounds</li>
                  <li>Stay focused on your exam screen</li>
                  <li>Use the chat for any queries or technical issues</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="stream-controls">
          <button onClick={handleStopStream} className="stop-button">
            Stop Exam
          </button>
        </div>
      </div>

      {/* Only show chat in exam mode */}
      {state.mode === "EXAM" && <MessageControls />}

      {/* Room Scan Dialog */}
      <RoomScanDialog isOpen={isRoomScanDialogOpen} onStart={startRoomScan} />

      {/* Clarification Dialog - Make sure it's rendered at root level */}
      {state.mode === "VERIFICATION" && (
        <ClarificationDialog
          isOpen={isClarificationDialogOpen}
          onSubmit={handleClarificationSubmit}
          onClose={() => {
            console.log("[VIEWER] Closing clarification dialog");
            setIsClarificationDialogOpen(false);
            setDetectedIssue(null);
            // Resume room scan when dialog is closed
            setRoomScanStarted(true);
            startRoomScan();
          }}
          detectedIssue={detectedIssue}
        />
      )}

      {/* ID Card Dialog */}
      {state.mode === "VERIFICATION" && (
        <IdCardDialog
          isOpen={isIdCardDialogOpen}
          onContinue={handleIdCardContinue}
        />
      )}
    </div>
  );
});

//------------------------------------------------------------------------------
async function startPlayer() {
  // Check if refs are available
  if (!state.localView?.current || !state.remoteView?.current) {
    console.error("[VIEWER/MASTER] View references are not available yet");
    return;
  }

  //dont remove
  // First check exam status - This is now our primary decision point
  try {
    const examStatusResponse = await fetch(
      `https://procbackend.elcarreira.com/exam/getExamStatus?studentId=${state.clientId}&examId=${window.appState.examId}`
    );

    if (!examStatusResponse.ok) {
      throw new Error("Failed to check exam status");
    }

    const examStatusData = await examStatusResponse.json();
    console.log("[VIEWER] Exam status check response:", examStatusData);

    if (examStatusData.examStatus === "completed") {
      console.log("[VIEWER] Exam already completed, cannot start");
      alert("This exam has already been completed. You cannot start it again.");
      return;
    }

    // Then check verification status
    const verificationResponse = await fetch(
      `https://procbackend.elcarreira.com/proctor/isStudentVerified?studentId=${state.clientId}&examId=${window.appState.examId}`
    );

    if (!verificationResponse.ok) {
      throw new Error("Failed to check verification status");
    }

    const verificationData = await verificationResponse.json();
    console.log("[VIEWER] Verification check response:", verificationData);

    // If exam is ongoing and student is verified, go directly to exam mode
    if (
      examStatusData.examStatus === "ongoing" &&
      verificationData.proctorverification === true
    ) {
      console.log(
        "[VIEWER] Exam is ongoing and student is verified, resuming exam"
      );
      state.mode = "EXAM";
      state.verificationStatus = "VERIFIED";
      state.isExamStarted = true;
      window.appState = {
        ...window.appState,
        isExamStarted: true,
        mode: "EXAM",
      };
    } else if (!window.appState.isExamStarted) {
      // If exam hasn't started, go through verification phase
      console.log("[VIEWER] Starting verification phase");
      state.mode = "VERIFICATION";
      state.isExamStarted = false;

      // Reset verification flags to ensure both checks are performed
      state.roomScanComplete = false;
      state.faceDetectionComplete = false;

      // Store proctor verification status
      state.proctorVerified = verificationData.proctorverification === true;
      // Keep verification status as PENDING until face and room scan complete
      state.verificationStatus = "PENDING";
    }

    // Then check termination status
    const terminationResponse = await fetch(
      `https://procbackend.elcarreira.com/auth/checkTermination?studentId=${state.clientId}&examId=${window.appState.examId}`
    );

    if (!terminationResponse.ok) {
      throw new Error("Failed to check termination status");
    }

    const terminationData = await terminationResponse.json();
    console.log("[VIEWER] Termination check response:", terminationData);

    if (terminationData.status === "terminated") {
      console.log("[VIEWER] Student is terminated from exam");
      alert(
        "You have been terminated from this exam due to multiple violations. Please contact your administrator."
      );
      return;
    }
  } catch (error) {
    console.error("[VIEWER] Error checking student status:", error);
    alert(
      "Unable to verify exam eligibility. Please try again or contact support."
    );
    return;
  }

  // Request fullscreen before starting exam
  if (state.role === OPTIONS.ROLE.VIEWER) {
    const fullscreenSuccess = await requestFullscreen();
    if (!fullscreenSuccess) {
      alert("Please allow fullscreen mode to start the exam");
      return;
    }

    // Initialize fullscreen monitoring
    initializeFullscreenMonitoring(() => {
      if (state.mode === "EXAM") {
        alert(
          "Warning: Exiting fullscreen mode during exam is not allowed. Please return to fullscreen mode."
        );
        // Try to request fullscreen again
        requestFullscreen().catch((err) => {
          console.error("Failed to re-enter fullscreen:", err);
        });
      }
    });
  }

  if (state.role === OPTIONS.ROLE.VIEWER) {
    const masterConnected = await checkMasterStatus();
    if (!masterConnected) {
      alert(
        "Master is not connected. Please wait for master to connect first."
      );
      return;
    }
  }

  state.playerIsStarted = true;
  if (state.role === "VIEWER") {
    startPlayerForViewer();
  } else {
    return;
  }
}

//------------------------------------------------------------------------------

async function startPlayerForViewer() {
  try {
    // Create KVS client
    console.log("Created KVS client...");

    // Add connection state monitoring with proper scope
    const handleConnectionFailure = async () => {
      console.log("[VIEWER] Connection lost, attempting reconnection...");

      // Only attempt reconnection if we're still in EXAM mode
      if (state.mode === "EXAM") {
        try {
          // Check verification status before reconnecting
          const verificationResponse = await fetch(
            `https://procbackend.elcarreira.com/proctor/isStudentVerified?studentId=${state.clientId}&examId=${window.appState.examId}`
          );

          if (!verificationResponse.ok) {
            throw new Error("Failed to check verification status");
          }

          const verificationData = await verificationResponse.json();
          console.log(
            "[VIEWER] Verification check before reconnection:",
            verificationData
          );

          if (!verificationData.proctorverification) {
            console.log(
              "[VIEWER] Student verification expired, ending session"
            );
            window.handleSessionEnd("VERIFICATION_EXPIRED");
            return;
          }

          // Wait 2 seconds before attempting reconnection
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Try to reconnect up to 3 times
          let reconnectAttempts = 0;
          const maxReconnectAttempts = 3;

          while (reconnectAttempts < maxReconnectAttempts) {
            try {
              console.log(
                `[VIEWER] Reconnection attempt ${
                  reconnectAttempts + 1
                }/${maxReconnectAttempts}`
              );
              await startPlayerForViewer();
              console.log("[VIEWER] Reconnection successful");
              return true;
            } catch (err) {
              console.error("[VIEWER] Reconnection attempt failed:", err);
              reconnectAttempts++;
              if (reconnectAttempts < maxReconnectAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 2000));
              }
            }
          }

          // Only transition to upload if all reconnection attempts fail
          if (reconnectAttempts === maxReconnectAttempts) {
            console.log(
              "[VIEWER] All reconnection attempts failed, ending session"
            );
            window.handleSessionEnd("CONNECTION_FAILED");
          }
        } catch (err) {
          console.error("[VIEWER] Error in reconnection process:", err);
          window.handleSessionEnd("CONNECTION_FAILED");
        }
      }
    };

    // Create KVS client
    const kinesisVideoClient = new KinesisVideo({
      region: config.aws.region,
      endpoint: state.endpoint || null,
      correctClockSkew: true,
      accessKeyId: config.aws.credentials.accessKeyId,
      secretAccessKey: config.aws.credentials.secretAccessKey,
    });

    // Get signaling channel ARN
    console.log("[VIEWER] Using channel name:", state.channelName);
    const describeSignalingChannelResponse = await kinesisVideoClient
      .describeSignalingChannel({
        ChannelName: state.channelName, // Already modified in state
      })
      .promise();

    const channelARN = describeSignalingChannelResponse.ChannelInfo.ChannelARN;
    console.log("[VIEWER] Channel ARN: ", channelARN);

    // Get signaling channel endpoints:
    console.log("Getting signaling channel endpoints...");
    const getSignalingChannelEndpointResponse = await kinesisVideoClient
      .getSignalingChannelEndpoint({
        ChannelARN: channelARN,
        SingleMasterChannelEndpointConfiguration: {
          Protocols: ["WSS", "HTTPS"],
          Role: state.role, //roleOption.MASTER
        },
      })
      .promise();

    const endpointsByProtocol =
      getSignalingChannelEndpointResponse.ResourceEndpointList.reduce(
        (endpoints, endpoint) => {
          endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
          return endpoints;
        }
      );
    console.log("[VIEWER] Endpoints: ", endpointsByProtocol);

    // Create Signaling Client
    console.log(`Creating signaling client...`);
    state.signalingClient = new SignalingClient({
      channelARN,
      channelEndpoint: endpointsByProtocol.WSS,
      role: state.role,
      region: config.aws.region,
      systemClockOffset: kinesisVideoClient.config.systemClockOffset,
      clientId: state.clientId,
      credentials: {
        accessKeyId: config.aws.credentials.accessKeyId,
        secretAccessKey: config.aws.credentials.secretAccessKey,
      },
    });

    // Get ICE server configuration
    console.log("Creating ICE server configuration...");
    const kinesisVideoSignalingChannelsClient =
      new KinesisVideoSignalingChannels({
        region: config.aws.region,
        endpoint: endpointsByProtocol.HTTPS,
        correctClockSkew: true,
        accessKeyId: config.aws.credentials.accessKeyId,
        secretAccessKey: config.aws.credentials.secretAccessKey,
      });

    console.log("Getting ICE server config response...");
    const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient
      .getIceServerConfig({
        ChannelARN: channelARN,
      })
      .promise((data) => {
        console.log("ICE PROMISE DATA ", data);
      })
      .catch((error) => {
        console.log("ICE ERROR ", error);
      });

    const iceServers = [];
    if (state.natTraversal === OPTIONS.TRAVERSAL.STUN_TURN) {
      console.log("Getting STUN servers...");
      iceServers.push({
        urls: `stun:stun.kinesisvideo.${config.aws.region}.amazonaws.com:443`,
      });
    }

    if (state.natTraversal !== OPTIONS.TRAVERSAL.DISABLED) {
      console.log("Getting TURN servers...");
      // Add null check for IceServerList
      if (getIceServerConfigResponse?.IceServerList) {
        getIceServerConfigResponse.IceServerList.forEach((iceServer) =>
          iceServers.push({
            urls: iceServer.Uris,
            username: iceServer.Username,
            credential: iceServer.Password,
          })
        );
      } else {
        console.warn("[VIEWER] No ICE servers received from Kinesis");
      }
    }

    const configuration = {
      iceServers:
        iceServers.length > 0
          ? iceServers
          : [
              // Fallback to Google's public STUN server if no ICE servers are configured
              { urls: "stun:stun.l.google.com:19302" },
            ],
      iceTransportPolicy:
        state.natTraversal === OPTIONS.TRAVERSAL.TURN_ONLY ? "relay" : "all",
    };

    const resolution =
      state.resolution === OPTIONS.RESOLUTION.WIDESCREEN
        ? { width: { ideal: 1280 }, height: { ideal: 720 } }
        : { width: { ideal: 640 }, height: { ideal: 480 } };

    const constraints = {
      video: state.sendVideo ? resolution : false,
      audio: state.sendAudio,
    };

    state.peerConnection = new RTCPeerConnection(configuration);
    if (state.openDataChannel) {
      console.log(`Opened data channel with MASTER.`);
      state.dataChannel =
        state.peerConnection.createDataChannel("kvsDataChannel");
      state.peerConnection.ondatachannel = (event) => {
        const dataChannel = event.channel;
        state.dataChannel = dataChannel;

        dataChannel.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            const timestamp = new Date().toISOString();
            console.log("[VIEWER] Raw message received:", event.data);

            switch (message.type) {
              case "MASTER_DISCONNECT":
                console.log(
                  "[VIEWER] Master disconnected, stopping viewer connection"
                );
                state.messages.broadcast += `${timestamp} - System: Master has disconnected\n`;
                stopPlayer();
                state.isMasterConnected = false;
                break;

              case "VERIFICATION_STATUS":
                // Only process if this message is for this student
                if (message.studentId === state.clientId) {
                  console.log(
                    "[VIEWER] Received verification status for this student:",
                    message
                  );

                  // Update both verification status and proctor verification state
                  const isAccepted = message.status === "ACCEPTED";
                  state.verificationStatus = isAccepted
                    ? "VERIFIED"
                    : "REJECTED";
                  state.proctorVerified = isAccepted;

                  if (isAccepted) {
                    console.log(
                      "[VIEWER] Student verified by proctor, waiting for exam to start"
                    );
                    // Only move to exam mode if all verifications are complete AND we're already in exam mode
                    if (
                      state.faceDetectionComplete &&
                      state.roomScanComplete &&
                      state.mode === "EXAM"
                    ) {
                      console.log(
                        "[VIEWER] All verifications complete and exam mode active, starting exam"
                      );
                      state.isExamStarted = true;
                      window.appState = {
                        ...window.appState,
                        isExamStarted: true,
                        mode: "EXAM",
                      };
                    } else {
                      console.log(
                        "[VIEWER] Waiting for all verifications or exam mode:",
                        {
                          faceComplete: state.faceDetectionComplete,
                          roomComplete: state.roomScanComplete,
                          currentMode: state.mode,
                        }
                      );
                    }
                  } else {
                    console.log(
                      "[VIEWER] Student verification rejected by proctor"
                    );
                    state.mode = "VERIFICATION";
                    state.isExamStarted = false;
                  }
                } else {
                  console.log(
                    "[VIEWER] Ignoring verification status for different student"
                  );
                }
                break;

              case "MODE_CHANGE":
                console.log("[VIEWER] Received mode change:", message.mode);
                if (message.mode === "exam") {
                  console.log("[VIEWER] Before mode change - Current state:", {
                    mode: state.mode,
                    isExamStarted: state.isExamStarted,
                    examStartTime: state.examStartTime,
                  });

                  // Update both mode and isExamStarted
                  state.mode = "EXAM";
                  state.isExamStarted = true;
                  state.examStartTime = new Date().toISOString();

                  // Update window.appState to ensure persistence
                  window.appState = {
                    ...window.appState,
                    mode: "EXAM",
                    isExamStarted: true,
                    examStartTime: state.examStartTime,
                  };

                  // Log current verification state and updated exam state
                  console.log("[VIEWER] After mode change - Updated state:", {
                    mode: state.mode,
                    isExamStarted: state.isExamStarted,
                    examStartTime: state.examStartTime,
                    verificationState: {
                      face: state.faceDetectionComplete,
                      room: state.roomScanComplete,
                      proctor: state.proctorVerified,
                    },
                  });

                  // Dispatch event to notify other components
                  window.dispatchEvent(
                    new CustomEvent("examStateChange", {
                      detail: {
                        mode: "EXAM",
                        isExamStarted: true,
                        timestamp: state.examStartTime,
                      },
                    })
                  );
                }
                break;

              case "CHAT":
                const loggedMessage = `${timestamp} - Master: ${message.message}\n`;
                console.log("[VIEWER] Received:", loggedMessage);

                // Route message to appropriate storage based on recipient
                if (message.recipient === "broadcast") {
                  state.messages.broadcast += loggedMessage;
                } else if (message.recipient === state.clientId) {
                  state.messages.master += loggedMessage;
                }

                // Update unread count if chat widget is not expanded
                if (!state.messages.isExpanded) {
                  state.messages.unreadCount =
                    (state.messages.unreadCount || 0) + 1;
                  console.log(
                    "[VIEWER] Updated unread count:",
                    state.messages.unreadCount
                  );
                }
                break;

              default:
                console.log("[VIEWER] Received unknown message type:", message);
            }
          } catch (e) {
            console.error("[VIEWER] Error processing message:", e);
          }
        };

        dataChannel.onclose = () => {
          console.log("[VIEWER] DataChannel closed");
        };

        dataChannel.onerror = (error) => {
          console.error("[VIEWER] DataChannel error:", error);
        };
      };

      // Poll for connection stats
      state.peerConnectionStatsInterval = setInterval(() => {
        state.peerConnection.getStats().then(onStatsReport);
      }, 1000);

      /// REVIEW BELOW HERE

      state.signalingClient.on("open", async () => {
        console.log("[VIEWER] Connected to signaling service");

        if (state.sendVideo || state.sendAudio) {
          try {
            state.localStream = await navigator.mediaDevices.getUserMedia(
              constraints
            );
            state.localStream
              .getTracks()
              .forEach((track) =>
                state.peerConnection.addTrack(track, state.localStream)
              );
            state.localView.current.srcObject = state.localStream;
          } catch (e) {
            console.error("[VIEWER] Could not find webcam");
            return;
          }
        }

        // Create an SDP offer to send to the master
        console.log("[VIEWER] Creating SDP offer");
        await state.peerConnection.setLocalDescription(
          await state.peerConnection.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
          })
        );

        // When trickle ICE is enabled, send the offer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
        if (state.useTrickleICE) {
          console.log("[VIEWER] Sending SDP offer");
          state.signalingClient.sendSdpOffer(
            state.peerConnection.localDescription
          );
        }
        // console.log("[VIEWER] Generating ICE candidates");
      });

      state.signalingClient.on("sdpAnswer", async (answer) => {
        // Add the SDP answer to the peer connection
        console.log("[VIEWER] Received SDP answer");
        await state.peerConnection.setRemoteDescription(answer);
      });

      state.signalingClient.on("iceCandidate", (candidate) => {
        // Add the ICE candidate received from the MASTER to the peer connection
        console.log("[VIEWER] Received ICE candidate");
        state.peerConnection.addIceCandidate(candidate);
      });

      state.signalingClient.on("close", () => {
        console.log("[VIEWER] Disconnected from signaling channel");
      });

      state.signalingClient.on("error", async (error) => {
        console.error("[VIEWER] Signaling client error: ", error);

        // Check if the error is due to channel not found
        if (
          error.message &&
          error.message.includes("ResourceNotFoundException")
        ) {
          console.log("[VIEWER] Channel not found, master likely disconnected");
          state.isMasterConnected = false;
          stopPlayer();
        }
      });

      // Send any ICE candidates to the other peer
      state.peerConnection.addEventListener("icecandidate", ({ candidate }) => {
        if (candidate) {
          console.log("[VIEWER] Generated ICE candidate");

          // When trickle ICE is enabled, send the ICE candidates as they are generated.
          if (state.useTrickleICE) {
            console.log("[VIEWER] Sending ICE candidate");
            state.signalingClient.sendIceCandidate(candidate);
          }
        } else {
          console.log("[VIEWER] All ICE candidates have been generated");

          // When trickle ICE is disabled, send the offer now that all the ICE candidates have ben generated.
          if (!state.useTrickleICE) {
            console.log("[VIEWER] Sending SDP offer");
            state.signalingClient.sendSdpOffer(
              state.peerConnection.localDescription
            );
          }
        }
      });

      // As remote tracks are received, add them to the remote view
      state.peerConnection.addEventListener("track", async (event) => {
        console.log("[VIEWER] Received remote track");
        if (state.remoteView.current.srcObject) {
          return;
        }
        state.remoteStream = event.streams[0];
        state.remoteView.current.srcObject = state.remoteStream;

        // Start continuous recording after a short delay
        setTimeout(async () => {
          console.log("[VIEWER] Attempting to start continuous recording");
          startContinuousRecording(state.localStream);
        }, 3000);
      });

      if (state.role === OPTIONS.ROLE.VIEWER) {
        // Add this event listener for the viewer
        state.signalingClient.on("sdpOffer", (offer, remoteClientId) => {
          console.log("Master has started streaming - SDP offer received");
        });
      }

      console.log("[VIEWER] Starting viewer connection");
      state.signalingClient.open();

      // Update the connection state monitoring
      state.peerConnection.oniceconnectionstatechange = async () => {
        const connectionState = state.peerConnection.iceConnectionState;
        console.log("[VIEWER] ICE Connection State:", connectionState);

        if (
          connectionState === "disconnected" ||
          connectionState === "failed"
        ) {
          handleConnectionFailure();
        }
      };
    }
  } catch (err) {
    console.error("[VIEWER] Error in startPlayerForViewer:", err);
    throw err;
  }
}

//------------------------------------------------------------------------------
function stopPlayer() {
  state.playerIsStarted = false;
  console.log(`role is '${state.role}'`);
  stopPlayerForViewer();
}

//------------------------------------------------------------------------------
function stopPlayerForViewer() {
  console.log("[VIEWER] Stopping viewer connection");

  // Preserve important state values before cleanup
  const preservedState = {
    mode: window.appState?.mode,
    examEndReason: window.appState?.examEndReason,
    isExamStarted: window.appState?.isExamStarted,
    role: window.appState?.role,
    clientId: window.appState?.clientId,
    examId: window.appState?.examId,
    channelName: window.appState?.channelName,
  };
  console.log("[VIEWER] Preserved state before cleanup:", preservedState);

  // Stop continuous recording
  stopContinuousRecording();

  // Clean up signaling client
  if (state.signalingClient) {
    state.signalingClient.close();
    state.signalingClient = null;
  }

  // Clean up peer connection
  if (state.peerConnection) {
    state.peerConnection.close();
    state.peerConnection = null;
  }

  // Clean up local stream
  if (state.localStream) {
    state.localStream.getTracks().forEach((track) => {
      track.stop();
      state.localStream.removeTrack(track);
    });
    state.localStream = null;
  }

  // Clean up remote stream
  if (state.remoteStream) {
    state.remoteStream.getTracks().forEach((track) => {
      track.stop();
      state.remoteStream.removeTrack(track);
    });
    state.remoteStream = null;
  }

  // Clear stats interval
  if (state.peerConnectionStatsInterval) {
    clearInterval(state.peerConnectionStatsInterval);
    state.peerConnectionStatsInterval = null;
  }

  // Clean up video elements
  if (state.localView && state.localView.current) {
    state.localView.current.srcObject = null;
  }

  if (state.remoteView && state.remoteView.current) {
    state.remoteView.current.srcObject = null;
  }

  // Clean up data channel
  if (state.dataChannel) {
    if (state.dataChannel.readyState === "open") {
      state.dataChannel.close();
    }
    state.dataChannel = null;
  }

  // Reset messages
  state.messages = {
    broadcast: "",
    master: "",
    currentView: "broadcast",
  };

  // Only reset player state but preserve exam completion state
  state.playerIsStarted = false;

  // Restore preserved state values
  window.appState = {
    ...preservedState,
    autoStart: false, // Ensure we don't auto-start again
  };
  console.log("[VIEWER] Restored state after cleanup:", window.appState);
}

//------------------------------------------------------------------------------
function onStatsReport(report) {
  // TODO: Publish stats
}

//------------------------------------------------------------------------------
function sendMessage() {
  const timestamp = new Date().toISOString();
  const messageObj = {
    type: "CHAT",
    message: state.messageToSend,
    sender: state.clientId,
    recipient: state.role === OPTIONS.ROLE.MASTER ? "broadcast" : "master",
  };

  if (state.role === OPTIONS.ROLE.MASTER) {
    Object.keys(state.dataChannelByClientId).forEach((clientId) => {
      try {
        if (state.dataChannelByClientId[clientId]?.readyState === "open") {
          state.dataChannelByClientId[clientId].send(
            JSON.stringify(messageObj)
          );
          console.log(
            `[MASTER] Message sent to client ${clientId}: ${state.messageToSend}`
          );
          // Add the sent message to broadcast messages
          state.messages.broadcast += `${timestamp} - You: ${state.messageToSend}\n`;
        }
      } catch (e) {
        console.error("[MASTER] Send DataChannel error:", e.toString());
      }
    });
  } else {
    if (state.dataChannel?.readyState === "open") {
      try {
        state.dataChannel.send(JSON.stringify(messageObj));
        console.log(`[VIEWER] Message sent to master: ${state.messageToSend}`);
        // Add the sent message to master messages
        state.messages.master += `${timestamp} - You: ${state.messageToSend}\n`;
      } catch (e) {
        console.error("[VIEWER] Send DataChannel error:", e.toString());
      }
    } else {
      console.warn("[VIEWER] Data channel not ready");
    }
  }

  // Clear the message input after sending
  state.messageToSend = "";
}
//------------------------------------------------------------------------------
// function updateState(key, value) {
//   // Don't store sensitive information in localStorage
//   const sensitiveKeys = ["accessKey", "secretAccessKey", "sessionToken"];
//   state[key] = value;

//   if (!sensitiveKeys.includes(key)) {
//     var localKey = `kvs-widget-${key}`;
//     localStorage.setItem(localKey, value);
//   }
// }

// function to send disconnect message to all viewers

// Add a function to check master's status
let lastCheckTime = 0;
const CHECK_INTERVAL = 8000; // 8 seconds in milliseconds

async function checkMasterStatus() {
  const now = Date.now();
  if (now - lastCheckTime < CHECK_INTERVAL) {
    // Skip if less than 8 seconds have passed since last check
    return state.isMasterConnected;
  }
  lastCheckTime = now;

  try {
    const kinesisVideoClient = new AWS.KinesisVideo({
      region: config.aws.region,
      accessKeyId: config.aws.credentials.accessKeyId,
      secretAccessKey: config.aws.credentials.secretAccessKey,
      sessionToken: null,
      correctClockSkew: true,
      maxRetries: 3,
    });

    console.log(
      "[VIEWER] Checking master status for channel:",
      state.channelName
    );

    // Simply try to describe the channel
    await kinesisVideoClient
      .describeSignalingChannel({
        ChannelName: state.channelName,
      })
      .promise();

    // If we get here, channel exists which means master is streaming
    console.log("[VIEWER] Master is connected, channel exists");
    state.isMasterConnected = true;
    return true;
  } catch (error) {
    if (error.code === "ResourceNotFoundException") {
      // Channel doesn't exist = master hasn't started streaming
      console.log("[VIEWER] Waiting for master to start streaming...");
    } else {
      console.error("[VIEWER] Error checking master status:", error);
    }
    state.isMasterConnected = false;
    return false;
  }
}

// section start-------------------video recording and storing to s3-----------------------------

// function to wait for specified duration
const wait = (timeInMs) =>
  new Promise((resolve) => setTimeout(resolve, timeInMs));

// function to start recording
async function startRecording(stream, lengthInMS = 10000) {
  try {
    const options = {
      mimeType: "video/webm;codecs=vp8,opus",
      videoBitsPerSecond: 1000000,
    };

    const recorder = new MediaRecorder(stream, options);
    const data = [];

    recorder.ondataavailable = (event) => data.push(event.data);
    recorder.start(5000); // Collect data in 5-second chunks
    console.log(`Recording started for ${lengthInMS / 1000} seconds...`);

    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = resolve;
      recorder.onerror = (event) => reject(event.name);
    });

    const recorded = wait(lengthInMS).then(() => {
      if (recorder.state === "recording") {
        recorder.stop();
      }
    });

    await Promise.all([stopped, recorded]);
    return new Blob(data, { type: "video/webm" });
  } catch (error) {
    console.error("Error during recording:", error);
    return null;
  }
}

// function to trigger download
// function downloadRecording(blob, timestamp) {
//   const url = window.URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.style.display = "none";
//   a.href = url;
//   a.download = `recording-${timestamp}.webm`;
//   document.body.appendChild(a);
//   a.click();
//   window.URL.revokeObjectURL(url);
//   document.body.removeChild(a);
// }

//function to handle S3 uploads

async function uploadToS3(blob, timestamp) {
  try {
    console.log("[VIEWER] Starting S3 upload for recording:", timestamp);

    // Create S3 client
    const s3 = new AWS.S3({
      region: config.aws.region,
      accessKeyId: config.aws.credentials.accessKeyId,
      secretAccessKey: config.aws.credentials.secretAccessKey,
    });

    const chunkNumber = String(Math.floor(Date.now() / 1000)).slice(-4);
    const key = `${state.universityId}/recordings/${state.channelName}/${state.clientId}/${state.clientId}_chunk${chunkNumber}_${timestamp}.webm`;
    console.log("chunk ", chunkNumber);
    console.log("key ", key);

    // Store the current chunk info for violation tracking
    state.currentChunkInfo = {
      chunkNumber,
      startTime: timestamp,
      key,
      size: blob.size,
      violations: [],
    };

    // Log current chunk info
    console.log("[VIEWER] Current recording chunk:", state.currentChunkInfo);

    const params = {
      Bucket: "talismaproctoring-system",
      Key: key,
      Body: blob,
      ContentType: "video/webm",
      Metadata: {
        "exam-id": state.channelName,
        "student-id": state.clientId,
        timestamp: timestamp,
        "chunk-number": chunkNumber,
        size: blob.size.toString(),
      },
    };

    // If there were violations in this chunk, log them and add to registry
    if (state.currentChunkInfo.violations.length > 0) {
      console.log(
        `[VIEWER] Chunk ${chunkNumber} contains violations:`,
        state.currentChunkInfo.violations
      );

      // Store in violation registry for later retrieval
      state.violationRegistry.push({
        ...state.currentChunkInfo,
        endTime: new Date().toISOString().replace(/:/g, "-"),
      });

      // Log entire violation registry
      console.log(
        "[VIEWER] Violation registry updated:",
        state.violationRegistry
      );
    }

    console.log("[VIEWER] Uploading to S3...", key);
    const result = await s3.putObject(params).promise();
    console.log("[VIEWER] Upload successful for chunk:", chunkNumber, result);

    return true;
  } catch (error) {
    console.error("[VIEWER] Error uploading to S3:", error);
    return false;
  }
}

// Function to track violations within the current chunk
function trackViolation(violationType, details) {
  console.log("[VIEWER] trackViolation called with:", {
    violationType,
    details,
  });

  if (!state.currentChunkInfo) {
    console.log("[VIEWER] No active recording chunk, creating a temporary one");
    // Create a temporary chunk info if none exists
    const tempChunkNumber = String(Math.floor(Date.now() / 1000)).slice(-4);
    state.currentChunkInfo = {
      chunkNumber: state.currentChunkInfo.chunkNumber || tempChunkNumber,
      startTime: new Date().toISOString(),
      key: state.currentChunkInfo.key,
      violations: [],
    };
  }

  // Restructure and simplify the violation object
  const violation = {
    details: {
      timestamp: new Date().toISOString(),
      type: violationType,
      message:
        details?.details?.message || details?.message || "Unknown violation",
      detectedDetails: details?.details?.detectedDetails || [],
      count: details?.details?.count || 1,
    },
  };

  // Add to current chunk's violations
  state.currentChunkInfo.violations.push(violation);

  // Log the violation with chunk info
  console.log(
    `[VIEWER] Violation detected in chunk ${state.currentChunkInfo.chunkNumber}:`,
    violation
  );

  // Prepare and send violation to backend immediately
  try {
    let flagPayload = {
      studentId: state.clientId,
      examId: localStorage.getItem("examId"),
      type: "suspicious_behavior",
      timeStamp: violation.details.timestamp,
      key: state.currentChunkInfo.key,
    };

    // Add appropriate detection array based on violation type
    if (
      violationType === "behavior_alert" ||
      violationType === "face_metrics"
    ) {
      flagPayload.actionDetection = [
        {
          action: details?.type || details?.alertType || "suspicious_behavior",
          timestamp: violation.details.timestamp,
          chunkId: state.currentChunkInfo.chunkNumber,
          key: state.currentChunkInfo.key,
        },
      ];
    } else if (
      violationType === "prohibited_objects" ||
      violationType === "object_detection"
    ) {
      // Extract object details
      // Extract object details
      const detectedObjects = details?.details?.detectedDetails || [];
      flagPayload.objectDetection = [
        {
          chunkId: state.currentChunkInfo.chunkNumber,
          key: state.currentChunkInfo.key,
          labels: detectedObjects.map((obj) => ({
            object: obj.category || obj.name || "unknown_object",
            confidence: obj.confidence ? obj.confidence / 100 : 0.7, // Convert percentage to decimal
            timestamp: violation.details.timestamp,
          })),
        },
      ];
    }

    // Log the payload being sent
    console.log("[VIEWER] Sending violation to backend:", flagPayload);

    // Send to backend
    fetch("https://procbackend.elcarreira.com/proctor/updateFlag", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(flagPayload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Backend request failed: ${response.status}`);
        }
        console.log("[VIEWER] Successfully sent violation to backend");
      })
      .catch((error) => {
        console.error("[VIEWER] Error sending violation to backend:", error);
      });
  } catch (error) {
    console.error("[VIEWER] Error preparing violation data:", error);
  }

  return {
    ...violation,
    chunkNumber: state.currentChunkInfo.chunkNumber,
    chunkKey: state.currentChunkInfo.key,
  };
}

// Simplified continuous recording function
async function startContinuousRecording(stream) {
  console.log("[VIEWER] Starting continuous recording process");
  if (state.isRecording) {
    console.log("[VIEWER] Recording already in progress, skipping");
    return;
  }

  // Set recording flag to true
  state.isRecording = true;

  // Initialize violation registry if not already
  if (!state.violationRegistry) {
    state.violationRegistry = [];
  }

  // Define a recordSegment function that will be called recursively
  async function recordSegment() {
    if (!state.isRecording) {
      console.log("[VIEWER] Recording stopped, ending recording loop");
      return;
    }

    try {
      console.log("[VIEWER] Beginning new recording segment");
      const recordedBlob = await startRecording(stream, 10000); // 10 seconds

      if (recordedBlob) {
        const size = (recordedBlob.size / (1024 * 1024)).toFixed(2);
        console.log(`[VIEWER] Segment recording completed. Size: ${size}MB`);

        // Upload to S3 with meaningful naming
        const timestamp = new Date().toISOString().replace(/:/g, "-");
        console.log(`[VIEWER] Uploading segment to S3 at ${timestamp}`);

        try {
          const uploaded = await uploadToS3(recordedBlob, timestamp);
          console.log(
            "[VIEWER] S3 upload result:",
            uploaded ? "Success" : "Failed"
          );
        } catch (uploadError) {
          console.error("[VIEWER] S3 upload error:", uploadError);
          // Continue recording even if upload fails
        }
      } else {
        console.warn("[VIEWER] No blob recorded, possible recording failure");
      }
    } catch (error) {
      console.error("[VIEWER] Error in recording segment:", error);
    } finally {
      // Always try to continue recording if state.isRecording is still true
      if (state.isRecording) {
        console.log("[VIEWER] Starting next recording segment...");
        // Use setTimeout to prevent immediate recursion and give a small break
        setTimeout(recordSegment, 500);
      }
    }
  }

  console.log("[VIEWER] Initiating first recording segment");
  recordSegment();
}

// function to stop continuous recording
function stopContinuousRecording() {
  console.log("[VIEWER] Stopping continuous recording");
  state.isRecording = false;
}

// section end -----video recording and storing------------------------

// S3_bucket/u001/exam_videos/dsa0_'date'_'randomid'/studentId/studentId_chunk1_timestamp

export default KinesisWebRTC;
